import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { routeConstant } from "shared/routes/routeConstant";
import { GetUserDashboardWaitingRevisionsService } from "shared/services/user";
import { MarkRevisionViewedService } from "shared/services/revision";
import { DismissReadyToStartService } from "shared/services/project";
import { handleErrors } from "shared/utils/helper";
import { ProjectDetailTabs } from "shared/utils/enum";
import { useSelector } from "react-redux";
import {
  GetNotificationsService,
  ReadNotificationService,
} from "shared/services/notification";
import { resolveNotificationTarget } from "shared/utils/notificationNavigation";
import { resolveNotificationPresentation } from "shared/utils/notificationPresentation";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./styles.module.scss";
import CustomLinkHeader from "shared/components/common/customLinkHeader";
import CustomPageHeading from "shared/components/common/customPageHeading";
import ProjectsList from "shared/components/dashboardCommon/projectsList";
import ProjectNotificationBannerCarousel, {
  BannerItem,
} from "shared/components/dashboardCommon/projectNotificationBannerCarousel";
import ListItemSkeleton from "shared/components/common/listItemSkeleton";

const DesignerHome = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [waitingRevisions, setWaitingRevisions] = useState<any[]>([]);
  const [loadingRevisions, setLoadingRevisions] = useState(true);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(true);
  const [projects, setProjects] = useState<any[]>([]);
  const auth = useSelector((state: any) => state.root.auth);

  const handleProjectsLoaded = (loaded: any[]) => setProjects(loaded);

  const navigateToProject = (
    project: any,
    options: { tab?: ProjectDetailTabs } = {},
  ) => {
    const path = routeConstant.designer.projectDetail.path.replace(
      ":id",
      String(project.project_id),
    );
    navigate(options.tab ? `${path}?tab=${options.tab}` : path, {
      state: { projectData: project },
    });
  };

  // Swiping a card away marks the latest revision viewed so the dismissal
  // sticks per user across reloads.
  const dismissBanner = (p: any) => {
    const revisionId = p.latest_revision?.revision_id;
    if (revisionId) {
      MarkRevisionViewedService(Number(revisionId)).catch(() => {});
    }
  };

  // Persists the "Ready to Start" dismissal so the card stays gone across reloads.
  const dismissReadyToStart = (p: any) => {
    if (p?.project_id) {
      DismissReadyToStartService(Number(p.project_id)).catch(() => {});
    }
  };

  // Clicking an activity opens the same destination the notifications screen would.
  const handleActivityClick = (item: any) => {
    const notificationId = item?.notification_id || item?.id;
    if (notificationId && !item?.is_read) {
      ReadNotificationService(Number(notificationId))
        .then(() =>
          window.dispatchEvent(new Event("notification:refresh-count")),
        )
        .catch(() => {});
    }

    const target = resolveNotificationTarget(item, location.pathname);
    if (target) {
      navigate(target.path, { state: target.state });
    }
  };

  // One pop-up card per project needing this designer's attention:
  //  - in_review          → client requested changes, revision back in queue
  //  - preliminary_finals → client approved preliminary; prelim finals ready
  //                         to begin (designer/both roles)
  //  - final_review       → client selected final review; ready for the final
  //                         reviewer (all designer roles — per client ruling
  //                         2026-07-18 "designer" has the same permissions as
  //                         "both")
  // Phase banners key off the latest revision being force-completed by the
  // status change; viewed_by_current_user gates all three per user.
  const designerItems: BannerItem[] = projects
    .map((p: any) => {
      const projectStatus = (p.status || "").toString().toLowerCase();
      const role = (p.role || "").toString().toLowerCase();

      if (projectStatus === "completed" || projectStatus === "archived") {
        return null;
      }

      // Ready to Start: preliminary project with no revision yet (dismissal persists).
      if (
        projectStatus === "preliminary" &&
        !p.latest_revision &&
        (role === "designer" || role === "both") &&
        !p.ready_to_start_dismissed
      ) {
        return {
          key: `${p.project_id}-ready-to-start`,
          title: `🎉 ${p.name || "Project"} is ready to start!`,
          description:
            "This project has moved into the preliminary phase. Upload your first revision to get started.",
          buttonText: "Upload Revision",
          onButtonClick: () =>
            navigate(
              `${routeConstant.designer.home.uploadRevision.path}?projectId=${p.project_id}`,
              {
                state: { projectId: p.project_id, projectTitle: p.name },
              },
            ),
          onDismiss: () => dismissReadyToStart(p),
          sortTime: p.created_at,
        };
      }

      if (!p.latest_revision || p.latest_revision.viewed_by_current_user) {
        return null;
      }

      const revisionStatus = (p.latest_revision.status || "")
        .toString()
        .toLowerCase()
        .replace(/[_\s-]/g, "");

      if (revisionStatus === "inreview") {
        return {
          key: `${p.project_id}-in-review`,
          title: `🛠 ${p.name || "Project"} - ${
            p.latest_revision?.title || "Latest Revision"
          }: Client requested changes — back in your queue!`,
          description:
            "Open the latest revision to review the client's feedback and start working.",
          buttonText: "Open Latest Revision Request",
          // The only action: open the Revision Requests tab, where the client's
          // requested changes live, and mark it viewed, retiring the card.
          onButtonClick: () =>
            navigateToProject(p, { tab: ProjectDetailTabs.FEEDBACK_REQUEST }),
          onDismiss: () => dismissBanner(p),
          sortTime: p.latest_revision?.created_at || p.created_at,
        };
      }

      if (revisionStatus === "completed") {
        if (
          projectStatus === "preliminary_finals" &&
          (role === "designer" || role === "both")
        ) {
          return {
            key: `${p.project_id}-prelim-finals`,
            title: `🚀 ${p.name || "Project"}: The preliminary finals are ready to begin!`,
            description:
              "The client approved the preliminary plans. Open the project to start on the preliminary finals.",
            buttonText: "Open Project",
            onButtonClick: () => navigateToProject(p),
            onDismiss: () => dismissBanner(p),
            sortTime: p.latest_revision?.created_at || p.created_at,
          };
        }
        if (
          projectStatus === "final_review" &&
          (role === "designer" || role === "final_reviewer" || role === "both")
        ) {
          return {
            key: `${p.project_id}-final-review`,
            title: `✅ ${p.name || "Project"}: Ready for final review!`,
            description:
              "The client is satisfied with the plans. Open the project to complete the internal review.",
            buttonText: "Open Project",
            onButtonClick: () => navigateToProject(p),
            onDismiss: () => dismissBanner(p),
            sortTime: p.latest_revision?.created_at || p.created_at,
          };
        }
      }

      return null;
    })
    .filter(Boolean)
    .sort(
      (a: any, b: any) =>
        new Date(b.sortTime || 0).getTime() -
        new Date(a.sortTime || 0).getTime(),
    )
    .map(({ sortTime, ...item }: any) => item as BannerItem);

  const fetchWaitingRevisions = async () => {
    try {
      setLoadingRevisions(true);
      const response = await GetUserDashboardWaitingRevisionsService();

      if (response.status === 200) {
        const revisions = response.data?.data || [];
        setWaitingRevisions(revisions);
      }
    } catch (error: any) {
      handleErrors(error);
      setWaitingRevisions([]);
    } finally {
      setLoadingRevisions(false);
    }
  };

  const fetchNotifications = useCallback(async () => {
    try {
      setLoadingNotifications(true);
      const response = await GetNotificationsService(1, 4);

      if (response.status === 200) {
        const data = response.data;
        const notificationsData = data?.data || [];
        setNotifications(notificationsData.slice(0, 4));
      }
    } catch (error: any) {
      handleErrors(error);
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, []);

  useEffect(() => {
    fetchWaitingRevisions();
    fetchNotifications();
  }, [fetchNotifications]);

  const getNotificationIcon = (type?: string) => {
    switch (type?.toLowerCase()) {
      case "project_created":
        return <Icons.ProjectCreatedIcon />;
      case "project_ongoing":
      case "revision_submitted":
        return <Icons.OnGoingState />;
      case "revision_approved":
        return <Icons.RevisionUploadedIcon />;
      case "comment_added":
      case "new_comment":
        return <Icons.NewCommentIcon />;
      case "reply_added":
      case "new_reply":
        return <Icons.NewReplyIcon />;
      case "project_completed":
        return <Icons.ProjectCompletedIcon />;
      case "project_onboarded":
        return <Icons.ProjectOnboardedIcon />;
      case "project_status_changed":
        return <Icons.ProjectStatusChangedIcon />;
      case "client_responded":
        return <Icons.ClientRespondedIcon />;
      case "project_designer_added":
        return <Icons.AddUsersIcon />;
      default:
        return <Icons.CheckMarkCircleIcon />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return `${diffInSeconds} seconds ago`;
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    return `${Math.floor(diffInSeconds / 86400)} days ago`;
  };

  return (
    <div className={classNames(styles.pageWrapper, "flex flex-col gap-5")}>
      <div className="sm:pr-0 pr-12">
        <CustomPageHeading
          size="large"
          title={`Welcome back, ${auth?.user?.display_name || ""}!`}
          description="Your creative workspace — all current builds in one place."
        />
      </div>

      {designerItems.length > 0 && (
        <ProjectNotificationBannerCarousel items={designerItems} />
      )}

      <div
        className={classNames(
          styles.contentWrapper,
          "grid grid-cols-12 gap-6 w-full",
        )}
      >
        <div
          className={classNames(
            styles.leftSideWrapper,
            "lg:col-span-8 col-span-12",
          )}
        >
          <div className="flex flex-col w-full">
            <CustomLinkHeader
              title="Working Projects"
              linkText="View All"
              linkHref={routeConstant.designer.allProjects.path}
            />

            <div className="mt-4 h-full">
              <ProjectsList
                showPageHeading={false}
                detailRoutePath={routeConstant.designer.projectDetail.path}
                EnableApiIntegration={true}
                limit={10}
                showEmptyState={true}
                noContentMessage="No working projects at the moment."
                containerClassName="flex flex-col gap-4"
                onlyMyProjects={true}
                onProjectsLoaded={handleProjectsLoaded}
              />
            </div>
          </div>
        </div>

        <div
          className={classNames(
            styles.rightSidebar,
            "lg:col-span-4 col-span-12 flex flex-col gap-6",
          )}
        >
          <div className={classNames(styles.box, styles.waitingOnClientBox)}>
            <CustomLinkHeader
              title="Pending Client Feedback"
              linkText="View All"
              linkHref={routeConstant.designer.allProjects.path}
              linkState={{
                filterRevisionStatus: "waiting_for_client_response",
                onlyMyProjects: true,
              }}
            />

            <div>
              {loadingRevisions ? (
                <ListItemSkeleton count={3} showIcon={true} />
              ) : waitingRevisions.length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  No revisions waiting for client response
                </p>
              ) : (
                <div
                  className={classNames(
                    styles.waitingOnClientList,
                    "flex flex-col gap-3 mt-4 pt-2",
                  )}
                >
                  {waitingRevisions.map((revision) => (
                    <CustomPageHeading
                      key={revision.revision_id}
                      size="xsmall"
                      isHandleIcon={true}
                      iconType="pending"
                      title={
                        revision.project_name
                          ? `${revision.project_name} - ${revision.name}`
                          : revision.name
                      }
                      description={
                        revision.status === "waiting_for_client_response"
                          ? "Pending Client Feedback"
                          : revision.status
                              .split("_")
                              .map(
                                (word: string) =>
                                  word.charAt(0).toUpperCase() + word.slice(1),
                              )
                              .join(" ")
                      }
                      descriptionStyle={classNames(styles.clientDescription)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className={classNames(styles.box, styles.recentActivityBox)}>
            <CustomLinkHeader
              title="Recent Activity"
              linkText="View All"
              linkHref={routeConstant.designer.notifications.path}
            />
            <div
              className={classNames(
                styles.activityContainer,
                "mt-4 pt-2 flex flex-col xs:gap-3 gap-2",
              )}
            >
              {loadingNotifications ? (
                <ListItemSkeleton count={3} showIcon={true} />
              ) : notifications.length === 0 ? (
                <p className="text-center text-gray-500 py-4">
                  No recent activity
                </p>
              ) : (
                notifications.slice(0, 4).map((item: any, index: number) => {
                  const notificationType = item.type || "approved";
                  const icon = getNotificationIcon(notificationType);
                  const { title, tone } = resolveNotificationPresentation(
                    item.type,
                    item.title,
                  );
                  const description =
                    item.project?.name ||
                    item.generator?.display_name ||
                    item.message ||
                    "";
                  const timeAgo = formatTimeAgo(
                    item.created_at || item.createdAt || item.timestamp,
                  );

                  return (
                    <button
                      type="button"
                      className={classNames(
                        styles.activityItem,
                        "flex gap-2 flex-wrap",
                      )}
                      key={item.notification_id || item.id || index}
                      onClick={() => handleActivityClick(item)}
                    >
                      <span
                        className={classNames(
                          styles.activityItemIcon,
                          tone !== "default" && styles[`tone${tone}`],
                        )}
                      >
                        {icon}
                      </span>
                      <CustomPageHeading
                        size="xsmall"
                        title={title}
                        descriptionStyle={styles.clientDescription}
                        description={
                          <div className="flex items-center gap-1 flex-wrap">
                            {description}{" "}
                            {description && timeAgo && (
                              <>
                                <span className="w-1 h-1 inline-block rounded-full bg-gray-500"></span>{" "}
                              </>
                            )}
                            {timeAgo}
                          </div>
                        }
                      />
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignerHome;
